
import React from 'react';
import { Trash2, X } from 'lucide-react';
import { useLanguage } from '../i18nContext';

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  itemType: string; // e.g., "Recording", "Folder", "Scheme"
}

export default function DeleteModal({ isOpen, onClose, onConfirm, title, description, itemType }: DeleteModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  // Translate itemType
  const getLocalizedItemType = () => {
    if (itemType === 'Folder') return t.dashboard.folders.replace('文件夹', '').trim() || 'Folder';
    if (itemType === 'File') return t.dashboard.newRecording.replace('新建', '').replace('New ', '').trim() || 'File';
    return itemType;
  };

  const localizedItemType = getLocalizedItemType();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 font-sans text-brand-900">
      {/* Backdrop - Neutral Dark as requested */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
      ></div>

      {/* Modal Card */}
      <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl p-8 animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 border border-surface-border flex flex-col items-center text-center">

        {/* Close Button (Top Right) */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-ink-muted hover:text-brand-900 hover:bg-surface rounded-full transition-colors"
        >
          <X size={20} />
        </button>

        {/* 1. Static, Professional Icon - No Animation, Restrained Style */}
        <div className="mb-6">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center border border-red-100">
                <Trash2 size={28} className="text-feedback-error" strokeWidth={1.5} />
            </div>
        </div>

        {/* 2. Content */}
        <h2 className="text-2xl font-serif font-bold text-brand-900 mb-3 tracking-tight">
          {t.deleteModal.deleteTitle} {localizedItemType}?
        </h2>

        <p className="text-ink-secondary text-sm leading-relaxed mb-8 max-w-[85%]">
          {description || `${t.deleteModal.deleteDescription} "${title}"。${t.deleteModal.cannotUndo}`}
        </p>

        {/* 3. Explicit Actions */}
        <div className="flex items-center gap-3 w-full">
            {/* Cancel Button */}
            <button
                onClick={onClose}
                className="flex-1 py-3.5 px-4 rounded-xl font-bold text-sm bg-white text-ink-dark hover:bg-surface hover:text-brand-900 border border-surface-border transition-all duration-200"
            >
                {t.deleteModal.cancel}
            </button>

            {/* Delete Button */}
            <button
                onClick={onConfirm}
                className="flex-1 py-3.5 px-4 rounded-xl font-bold text-sm bg-feedback-error text-white shadow-md hover:bg-red-600 border border-transparent transition-all duration-200 flex items-center justify-center gap-2"
            >
                <Trash2 size={16} />
                {t.deleteModal.confirm}
            </button>
        </div>

      </div>
    </div>
  );
}
